// frontend/src/components/TiptapEmailEditor.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-12-03
// Updated:      2026-10-27
// 2026-10-27: Inline font family, size and colour formatting via Tiptap mark; HTML mode sync.
//
// Description:
//   Theme-aware rich text email editor using Tiptap.
//   - Fully working inline image insertion
//   - Proper heading/list/alignment support
//   - Primary-coloured 18px icons with transparent background
//   - Hover/active tint using primary hex
//   - HTML mode toggle
//   - No borders anywhere
//
// Responsive pass (2026-09-16):
//   - Mobile toolbar is a single horizontal scroll strip instead of wrapping into multiple rows.
//   - Desktop retains the existing wrapped toolbar layout.
//   - Editor shell and content use min-width containment to prevent horizontal modal overflow.
//   - Inline images are constrained to the editor width on all screen sizes.
//   - Long rich-text/HTML content can wrap without forcing the parent modal wider.
//   - No Tiptap extensions, commands, image insertion, HTML mode, value sync, or onChange logic changed.
//

// 2026-10-27 12:21 GMT: Restore ordered/unordered list markers in Tiptap (Tailwind Preflight reset).
import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";

import { useEditor, EditorContent } from "@tiptap/react";
import { Mark, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";

import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaListUl,
  FaListOl,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
  FaUndo,
  FaRedo,
  FaImage,
} from "react-icons/fa";

// Built-in PDF-compatible font families (avoids depending on licensed/installed font files).
const FONT_OPTIONS = [
  { value: "Arial, Helvetica, sans-serif", label: "Arial / Helvetica" },
  { value: "Times New Roman, Times, serif", label: "Times New Roman" },
  { value: "Courier New, Courier, monospace", label: "Courier New" },
];
const SIZE_OPTIONS = ["8", "9", "10", "11", "12", "14", "16", "18", "20", "24", "28", "32"];
const normalizeHex = (v) => /^#[0-9a-f]{6}$/i.test(v || "") ? v : "#000000";

// Persist formatting as inline HTML so editing, API persistence and PDF output agree.
const QuoteTextFormatting = Mark.create({
  name: "quoteTextFormatting",
  addAttributes() {
    return {
      fontFamily: { default: null, parseHTML: el => el.style.fontFamily || null },
      fontSize: { default: null, parseHTML: el => el.style.fontSize || null },
      color: { default: null, parseHTML: el => el.style.color || null },
    };
  },
  parseHTML() { return [{ tag: "span[style]" }]; },
  renderHTML({ HTMLAttributes }) {
    const styles = [];
    if (HTMLAttributes.fontFamily) styles.push(`font-family: ${HTMLAttributes.fontFamily}`);
    if (HTMLAttributes.fontSize) styles.push(`font-size: ${HTMLAttributes.fontSize}`);
    if (HTMLAttributes.color) styles.push(`color: ${HTMLAttributes.color}`);
    return ["span", mergeAttributes({ style: styles.join("; ") }), 0];
  },
});

// Convert hex → rgba for tint
const hexToRgba = (hex, alpha) => {
  const h = hex.replace("#", "");
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const FONT_SIZE_MAP = {
  small: "0.80rem",
  normal: "0.90rem",
  large: "1.05rem",
  huge: "1.18rem",
};

const ToolbarButton = ({ icon, onClick, primary, title }) => {
  const hover = hexToRgba(primary, 0.15);
  const active = hexToRgba(primary, 0.25);

  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
      style={{
        background: "transparent",
        color: primary,
        transition: "background 0.15s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = hover)}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      onMouseDownCapture={(e) =>
        (e.currentTarget.style.background = active)
      }
    >
      {icon}
    </button>
  );
};

const TiptapEmailEditor = ({
  value = "",
  onChange,
  placeholder = "Write your email…",
  readOnly = false,
  className = "",
  availableFonts = [],
}) => {
  const { theme } = useTheme() || {};
  const primary = theme?.primary_color || "#ff7a00";
  const bodyFont = theme?.font_body || "system-ui";

  const [html, setHtml] = useState(value || "");
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [editorFont, setEditorFont] = useState(bodyFont);
  const [fontSize, setFontSize] = useState("12");
  const [textColor, setTextColor] = useState("#000000");

  const isSettingRef = useRef(false);
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        history: true,
      }),
      Underline,
      QuoteTextFormatting,
      Image.configure({ inline: true }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],
    content: value || "<p></p>",
    editable: !readOnly,
    onUpdate({ editor }) {
      if (isSettingRef.current) return;
      const next = editor.getHTML();
      setHtml(next);
      onChange?.(next);
    },
  });

  // Sync external value → editor
  useEffect(() => {
    if (!editor) return;
    if (value !== html && !isSettingRef.current) {
      editor.commands.setContent(value || "<p></p>", false);
      setHtml(value);
    }
  }, [value, editor]);

  const toggleHtmlMode = () => {
    if (isHtmlMode && editor) {
      editor.commands.setContent(html || "<p></p>", false);
    } else if (editor) {
      setHtml(editor.getHTML());
    }
    setIsHtmlMode(prev => !prev);
  };

  const applyFormatting = (patch) => {
    if (!editor || readOnly) return;
    const current = editor.getAttributes("quoteTextFormatting") || {};
    editor.chain().focus().setMark("quoteTextFormatting", { ...current, ...patch }).run();
  };


  const insertImageFromFile = (file) => {
    if (!editor || !file) return;
    const reader = new FileReader();
    reader.onload = () => {
      editor.chain().focus().setImage({ src: reader.result }).run();
    };
    reader.readAsDataURL(file);
  };

  const iconSize = 18;

  const tool = {
    bold: () => editor.chain().focus().toggleBold().run(),
    italic: () => editor.chain().focus().toggleItalic().run(),
    underline: () => editor.chain().focus().toggleUnderline().run(),
    h1: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    h2: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    h3: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    ul: () => editor.chain().focus().toggleBulletList().run(),
    ol: () => editor.chain().focus().toggleOrderedList().run(),
    left: () => editor.chain().focus().setTextAlign("left").run(),
    center: () => editor.chain().focus().setTextAlign("center").run(),
    right: () => editor.chain().focus().setTextAlign("right").run(),
    justify: () => editor.chain().focus().setTextAlign("justify").run(),
    undo: () => editor.chain().focus().undo().run(),
    redo: () => editor.chain().focus().redo().run(),
    image: () => fileInputRef.current?.click(),
  };

  const tintHover = hexToRgba(primary, 0.1);
  // The toolbar reflects the selection (including existing saved formatting).
  useEffect(() => {
    if (!editor) return undefined;
    const sync = () => {
      const attrs = editor.getAttributes("quoteTextFormatting") || {};
      setEditorFont(attrs.fontFamily || FONT_OPTIONS[0].value);
      setFontSize(String(parseInt(attrs.fontSize || "12", 10) || 12));
      const raw = attrs.color || "";
      if (/^#[0-9a-f]{6}$/i.test(raw)) setTextColor(raw);
      else if (/^rgb\(/i.test(raw)) {
        const parts = raw.match(/\d+/g);
        if (parts?.length >= 3) setTextColor("#" + parts.slice(0,3).map(v => Number(v).toString(16).padStart(2,"0")).join(""));
      }
    };
    editor.on("selectionUpdate", sync);
    editor.on("transaction", sync);
    return () => { editor.off("selectionUpdate", sync); editor.off("transaction", sync); };
  }, [editor]);

  return (
    <div
      className={`tiptap-email-editor w-full min-w-0 rounded-xl border border-gray-200 bg-white p-3 sm:p-4 ${className}`}
      style={{ fontFamily: bodyFont }}
    >
      {/* Toolbar */}
      <div className="-mx-1 mb-2 flex min-w-0 flex-nowrap items-center gap-1 overflow-x-auto px-1 pb-1 sm:mx-0 sm:flex-wrap sm:gap-2 sm:overflow-visible sm:px-0 sm:pb-0">

        <select
          aria-label="Font family"
          title="Font family"
          value={FONT_OPTIONS.some(f => f.value === editorFont) ? editorFont : FONT_OPTIONS[0].value}
          disabled={readOnly || isHtmlMode}
          onChange={(e) => { setEditorFont(e.target.value); applyFormatting({ fontFamily: e.target.value }); }}
          className="h-8 max-w-[145px] shrink-0 rounded-md border border-gray-200 bg-white px-1 text-xs"
        >
          {FONT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <select
          aria-label="Font size"
          title="Font size (pt)"
          value={SIZE_OPTIONS.includes(fontSize) ? fontSize : "12"}
          disabled={readOnly || isHtmlMode}
          onChange={(e) => { setFontSize(e.target.value); applyFormatting({ fontSize: `${e.target.value}pt` }); }}
          className="h-8 w-[62px] shrink-0 rounded-md border border-gray-200 bg-white px-1 text-xs"
        >
          {SIZE_OPTIONS.map(v => <option key={v} value={v}>{v} pt</option>)}
        </select>
        <label className="flex h-8 shrink-0 items-center gap-1 text-xs" title="Text colour">
          <span>Colour</span>
          <input aria-label="Text colour" type="color" value={normalizeHex(textColor)}
            disabled={readOnly || isHtmlMode}
            onChange={e => { setTextColor(e.target.value); applyFormatting({ color: e.target.value }); }}
            className="h-7 w-8 cursor-pointer border-0 bg-transparent p-0" />
        </label>

        <ToolbarButton
          icon={<FaBold size={iconSize} />}
          onClick={tool.bold}
          primary={primary}
        />
        <ToolbarButton
          icon={<FaItalic size={iconSize} />}
          onClick={tool.italic}
          primary={primary}
        />
        <ToolbarButton
          icon={<FaUnderline size={iconSize} />}
          onClick={tool.underline}
          primary={primary}
        />

        <ToolbarButton
          icon={<span style={{ fontSize: 14, fontWeight: 700 }}>H1</span>}
          onClick={tool.h1}
          primary={primary}
        />
        <ToolbarButton
          icon={<span style={{ fontSize: 14, fontWeight: 700 }}>H2</span>}
          onClick={tool.h2}
          primary={primary}
        />
        <ToolbarButton
          icon={<span style={{ fontSize: 14, fontWeight: 700 }}>H3</span>}
          onClick={tool.h3}
          primary={primary}
        />

        <ToolbarButton
          icon={<FaListUl size={iconSize} />}
          onClick={tool.ul}
          primary={primary}
        />
        <ToolbarButton
          icon={<FaListOl size={iconSize} />}
          onClick={tool.ol}
          primary={primary}
        />

        <ToolbarButton
          icon={<FaAlignLeft size={iconSize} />}
          onClick={tool.left}
          primary={primary}
        />
        <ToolbarButton
          icon={<FaAlignCenter size={iconSize} />}
          onClick={tool.center}
          primary={primary}
        />
        <ToolbarButton
          icon={<FaAlignRight size={iconSize} />}
          onClick={tool.right}
          primary={primary}
        />
        <ToolbarButton
          icon={<FaAlignJustify size={iconSize} />}
          onClick={tool.justify}
          primary={primary}
        />

        <ToolbarButton
          icon={<FaUndo size={iconSize} />}
          onClick={tool.undo}
          primary={primary}
        />
        <ToolbarButton
          icon={<FaRedo size={iconSize} />}
          onClick={tool.redo}
          primary={primary}
        />

        <ToolbarButton
          icon={<FaImage size={iconSize} />}
          onClick={tool.image}
          primary={primary}
        />

        {/* HTML MODE TOGGLE */}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            toggleHtmlMode();
          }}
          className="shrink-0 rounded-md px-3 py-[6px] text-xs font-medium"
          style={{
            background: isHtmlMode ? tintHover : "transparent",
            color: primary,
          }}
        >
          {isHtmlMode ? "Rich Text" : "HTML"}
        </button>
      </div>

      {/* Hidden image input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => insertImageFromFile(e.target.files?.[0])}
      />

      {/* Editor or HTML */}
      {!isHtmlMode && (
        <div className="relative min-h-[200px] min-w-0 max-h-[45dvh] overflow-auto rounded-md border border-gray-200 p-3 sm:max-h-[350px]">
          <EditorContent
            editor={editor}
            className="min-w-0"
            style={{
              fontFamily: editorFont,
              fontSize: FONT_SIZE_MAP.normal,
            }}
          />
          {!editor?.getText() && (
            <div className="pointer-events-none absolute left-3 top-3 text-xs text-gray-400">
              {placeholder}
            </div>
          )}
        </div>
      )}

      {isHtmlMode && (
        <textarea
          value={html}
          onChange={(e) => {
            const next = e.target.value.trim();
            setHtml(next);
            onChange?.(next);
          }}
          spellCheck={false}
          className="min-h-[200px] max-h-[45dvh] w-full min-w-0 resize-y rounded-md border border-gray-200 p-3 font-mono text-xs sm:max-h-[350px]"
        />
      )}

      <style>{`
        .tiptap-email-editor,
        .tiptap-email-editor .ProseMirror {
          min-width: 0;
          max-width: 100%;
        }

        .tiptap-email-editor .ProseMirror {
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        /* Tailwind Preflight removes browser default list markers. Restore them
           inside the editable Tiptap document without affecting other UI. */
        .tiptap-email-editor .ProseMirror ol {
          list-style-type: decimal;
          list-style-position: outside;
          margin: 0.35em 0;
          padding-left: 1.7em;
        }

        .tiptap-email-editor .ProseMirror ul {
          list-style-type: disc;
          list-style-position: outside;
          margin: 0.35em 0;
          padding-left: 1.7em;
        }

        .tiptap-email-editor .ProseMirror li {
          padding-left: 0.2em;
        }

        .tiptap-email-editor .ProseMirror li > p {
          margin: 0;
        }

        .tiptap-email-editor .ProseMirror img {
          display: inline-block;
          max-width: 100%;
          height: auto;
        }

        .tiptap-email-editor .ProseMirror pre,
        .tiptap-email-editor .ProseMirror code {
          max-width: 100%;
          white-space: pre-wrap;
          overflow-wrap: anywhere;
        }
      `}</style>
    </div>
  );
};

export default TiptapEmailEditor;
